"use client";

import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { jwtDecode } from "jwt-decode";
import { getAccess, logout as clearTokens } from "@/utils/tokens";

const piStore = {
  get: (k: string) => {
    try { return typeof window === "undefined" ? null : sessionStorage.getItem(k); }
    catch { return null; }
  },
  set: (k: string, v: string) => {
    try { if (typeof window !== "undefined") sessionStorage.setItem(k, v); }
    catch {}
  },
  del: (k: string) => {
    try { if (typeof window !== "undefined") sessionStorage.removeItem(k); }
    catch {}
  },
};

export type AuthUser = {
  id?: string;
  _id?: string;
  email?: string;
  username?: string;
  piUsername?: string;
  piUid?: string;
  pi_uid?: string; // legacy
  role?: "user" | "creator" | "admin" | "premium" | string;
  exp?: number;
  [k: string]: any;
};

type AuthContextValue = {
  user: AuthUser | null;
  token: string | null;
  piAccessToken: string | null;
  isAuthenticated: boolean;
  hydrated: boolean; // ✅ add
  login: (payloadOrToken?: any, maybePiToken?: string) => void;
  logout: () => void;
  reauthPi: () => Promise<boolean>;
  setPiToken: (t: string) => void;
  setRedirectPath: (path: string) => void;
  getRedirectPath: () => string;
};

const AuthContext = createContext<AuthContextValue | null>(null);

const PI_ACCESS_KEY = "piAccessToken";
const REDIRECT_KEY = "intendedPath";

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [piAccessToken, setPiAccessToken] = useState<string | null>(null);
const [hydrated, setHydrated] = useState(false);

const isPiTokenExpired = useCallback((t: string) => {
  // Many Pi access tokens are opaque (not JWT). Don’t force reauth loop.
  if (!t || t.split(".").length !== 3) return false;

  try {
    const decoded: any = jwtDecode(t);
    return Number(decoded?.exp) * 1000 < Date.now();
  } catch {
    return false;
  }
}, []);


  const logout = useCallback(() => {
    clearTokens();
   piStore.del(PI_ACCESS_KEY);
    localStorage.removeItem(REDIRECT_KEY);
    setUser(null);
    setToken(null);
    setPiAccessToken(null);
  }, []);

 const reauthPi = useCallback(async () => {
  const ready = (window as any).__piReady
    ? await ((window as any).__piReady as Promise<boolean>)
    : !!(window as any).Pi;

  if (!ready || !(window as any).Pi) return false;

  try {
    const scopes = ["username", "payments"];
    const auth = await (window as any).Pi.authenticate(scopes, () => {});
    if (!auth?.accessToken) throw new Error("No Pi reauth token");

    piStore.set(PI_ACCESS_KEY, auth.accessToken);
    setPiAccessToken(auth.accessToken);
    return true;
  } catch (e) {
    console.warn("Pi reauthentication failed:", e);

    // ✅ Only clear Pi token; do NOT log out JWT session
    piStore.del(PI_ACCESS_KEY);
    setPiAccessToken(null);

    return false;
  }
}, []);

  useEffect(() => {
    const access = getAccess();
    if (access) {
      try {
        setUser(jwtDecode<AuthUser>(access));
      } catch {}
      setToken(access);
    }

  const storedPi = piStore.get(PI_ACCESS_KEY);
if (storedPi) setPiAccessToken(storedPi);
 setHydrated(true); // ✅ add
  }, []);

  useEffect(() => {
    if (!piAccessToken) return;

    const interval = window.setInterval(async () => {
      if (isPiTokenExpired(piAccessToken)) {
  const isPiEnv =
    !!(window as any).Pi || (await ((window as any).__piReady as Promise<boolean> | undefined)) === true;

  if (!isPiEnv) {
    // Outside Pi Browser: don't force logout / reauth loop
    console.warn("Pi token expired but not in Pi Browser → skip reauth");
    return;
  }

  console.warn("Pi token expired → reauth...");
  await reauthPi();
}

    }, 60_000);

    return () => window.clearInterval(interval);
  }, [piAccessToken, isPiTokenExpired, reauthPi]);

  const login = useCallback((payloadOrToken?: any, maybePiToken?: string) => {
    let access: string | null = null;
    let piToken: string | undefined;

    if (typeof payloadOrToken === "string" || !payloadOrToken) {
      access = payloadOrToken || getAccess();
      piToken = maybePiToken;
    } else {
      access = payloadOrToken.access || getAccess();
      piToken = payloadOrToken.piToken;
    }

    if (access) {
      try {
        setUser(jwtDecode<AuthUser>(access));
      } catch {}
      setToken(access);
    }

  if (piToken) {
  piStore.set(PI_ACCESS_KEY, piToken);
  setPiAccessToken(piToken);
}

  }, []);

  const setRedirectPath = useCallback((path: string) => {
    localStorage.setItem(REDIRECT_KEY, path);
  }, []);

const getRedirectPath = useCallback(() => {
  const path = localStorage.getItem(REDIRECT_KEY);
  localStorage.removeItem(REDIRECT_KEY);

  if (!path || path === "/login") return "/app";
  return path;
}, []);

  // keep bridge for axios interceptor
  useEffect(() => {
    (window as any).__authCtx = { reauthPi, logout };
    return () => {
      try {
        delete (window as any).__authCtx;
      } catch {}
    };
  }, [reauthPi, logout]);

  const value: AuthContextValue = {
    user,
    token,
    piAccessToken,
      hydrated,                 // ✅ add
  isAuthenticated: !!token, // ✅ change
    login,
    logout,
    reauthPi,
   setPiToken: (t: string) => {
  piStore.set(PI_ACCESS_KEY, t);
  setPiAccessToken(t);
},
    setRedirectPath,
    getRedirectPath,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider />");
  return ctx;
}
